import { isInDateRange } from '../../../services/utilities';
import { operatingCountrySelector } from '../../../store/auth/selectors';
import { createLoadingSelector } from '../../api/selectors';
import { resourceSelectors } from '../../utils/resourceSelectors';

export const [
  configurationAllIdsSelector,
  configurationByIdSelector,
  allConfigurationSelector,
] = resourceSelectors('configuration');

// Shared and frozen, so a missing variable (e.g. before configuration loads)
// doesn't hand useSelector a new object on every call, and no caller can mutate it.
const NO_VARIABLE = Object.freeze({});

export const configurationVariableSelector = (key) => (state) => {
  return configurationByIdSelector(state)?.[key] || NO_VARIABLE;
};

export const seasonalDecorationSelector =
  configurationVariableSelector('seasonalDecoration');

/**
 * `true` once the initial GET /configuration has answered (success or
 * failure). Login flows wait for this before deciding which token provider a
 * new login uses.
 */
const configurationLoadingSelector = createLoadingSelector([
  'CONFIGURATION/INDEX',
]);
export const configurationSettledSelector = (state) =>
  !configurationLoadingSelector(state);

/**
 * The `authentication` configuration record: `{ useOidc: boolean }`. The flag
 * decides which provider a *new* login uses; a stored session always refreshes
 * and revokes against the provider that issued it (see services/tokens.js).
 */
export const authenticationConfigurationSelector =
  configurationVariableSelector('authentication');

export const useOidcSelector = (state) =>
  authenticationConfigurationSelector(state)?.useOidc === true;

export const assetClassMigrationSelector =
  configurationVariableSelector('assetClassMigration');

export const useNewAssetClassStructureSelector = (state) => {
  return assetClassMigrationSelector(state)?.useNewAssetClassStructure ?? true;
};

export const seasonalDecorationConfigSelector = (key) => (state) => {
  return seasonalDecorationSelector(state)?.[key] || null;
};

export const seasonalDecorationIsActiveSelector = (
  key,
  userCountryOverride
) => {
  return (state) => {
    const seasonalDecorationConfig =
      seasonalDecorationConfigSelector(key)(state);
    if (!seasonalDecorationConfig) {
      return false;
    }

    const {
      startDate,
      endDate,
      countries: configCountries,
    } = seasonalDecorationConfig;
    const userCountry = userCountryOverride ?? operatingCountrySelector(state);

    // If no user country is available, we consider it as matching all countries.
    // If a user country is available, we check if it is included in configCountries.
    const countryMatch =
      !userCountry ||
      !configCountries?.length ||
      configCountries.includes(userCountry);

    return isInDateRange(new Date(), startDate, endDate) && countryMatch;
  };
};

/**
 * The `referralCampaign` record: `{ enabled, countries, startDate, endDate,
 * url }`, toggled in AdminJS. Shown from startDate through endDate, never
 * without both. For customers and advisors of the selected countries: an
 * advisor's token carries their own country, also while impersonating, so
 * they see it whichever client they view. Not for leads.
 */
export const referralCampaignSelector =
  configurationVariableSelector('referralCampaign');

export const referralCampaignIsActiveSelector = (state) => {
  const { enabled, countries, startDate, endDate } =
    referralCampaignSelector(state);

  if (enabled !== true || !startDate || !endDate) return false;
  if (state.auth?.userType === 'lead') return false;

  return (
    (countries ?? []).includes(operatingCountrySelector(state)) &&
    isInDateRange(new Date(), startDate, endDate)
  );
};
